import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Button } from "../../../ui/Button";
import { ScorecardIcon } from "../../../assets/svg/ScorecardIcon";
import { TitleText } from "../../../ui/Text";
import { SettingsIcon } from "../../../assets/svg/SettingsIcon";
import { OrganisationTypeIcon } from "../../../assets/svg/OrganisationTypeIcon";
import { Field, Formik } from "formik";
import { GetHook, postEndpoint, putEndpoint } from "../../../api/api";
import { SCORECARD_URL, KPI_URL, PERIOD_URL, SCORECARD_KPI_TARGET } from "../../../api/urls";
import { get } from "../../../utils/get";
import moment from "moment";

import KpiParameters from "./KpiForms/KpiParameters";
import AlignmentOptions from "./KpiForms/AlignmentOptions";
import Allocation from "./KpiForms/Allocation";
import Axios from "../../../utils/axios";
import { toast } from "react-toastify";
import { AddKpiSchema } from "../../../utils/validationSchema/AddKpiSchema";
import axios from "axios";
interface IProps {
  kpi?: any;
  scorecard_id: string;
  editData?: any;
  isAddMode: any;
  isEdit: any;
  closeModal?: any;
}

const AddKpi = ({
  kpi,
  scorecard_id,
  editData,
  isEdit,
  isAddMode,
  closeModal,
}: IProps) => {
  const [step, setStep] = useState(1);
  const [createRes, createReq] = postEndpoint();
  const [updateRes, updateReq] = putEndpoint();
  const [scorecard, setScorecard] = useState<any>([]);
  const [period, setPeriod] = useState<any>([]);
  const [supportedKpis, setSupportedKpis] = useState([]);
  const [kpiType, setKpiType] = useState("");
  const formRef: any = useRef<any>(null);
  const [targets, settargets] = useState<any>([])
  const nextStep = () => {
    setStep(step + 1);
  };
  const previousStep = () => {
    setStep(step - 1);
  };

  const fetchScorecard = () => {
    Axios.get(`${SCORECARD_URL}`).then((res) => {
      const { returned_resultset } = get(res, "data");
      setScorecard(
        returned_resultset.filter((scorecard) => scorecard.id === scorecard_id)
      );
    });
  };

  const fetchSupportedKpi = () => {
    if (get(scorecard[0], "parent_id")) {
      Axios.get(`${SCORECARD_URL}/${get(scorecard[0], "parent_id")}/kpi`).then(
        (res) => {
          setSupportedKpis(res?.data?.returned_resultset);
        }
      );
    }
  };

  useEffect(() => {
    fetchScorecard();
    return () => {};
  }, []);

  useEffect(() => {
    if (scorecard.length > 0) {
      Axios.get(`${PERIOD_URL}`).then((res) => {
        const { returned_resultset } = get(res, "data");
        const id = get(scorecard[0], "period_id");

        setPeriod(returned_resultset.filter((period) => period.id === id));
      });
      fetchSupportedKpi();
    }
    return () => {};
  }, [scorecard]);

  const renderSteps = (values, setFieldValue) => {
    switch (step) {
      case 1:
        return (
          <KpiParameters
            kpiOptions={kpi}
            values={values}
            setFieldValue={setFieldValue}
            setKpiType={setKpiType}
          />
        );
      case 2:
        return <AlignmentOptions kpiOptions={supportedKpis} values={values} />;
      case 3:
        return <Allocation values={values} setFieldValue={setFieldValue} />;
    }
  };

  const getTargets = (values) => {
    if (
      get(values, "allocation.value") === "monthly" ||
      get(values, "allocation.value") === "monthly-constant" ||
      get(values, "allocation.value") === "prorata-monthly"
    ) {
      return moment.monthsShort().map((month) => {
        const target = {
          end_date: moment().month(month).endOf("month").format("YYYY-MM-DD"),
          label: month,
          // period_id: null,
          start_date: moment()
            .month(month)
            .startOf("month")
            .format("YYYY-MM-DD"),
          target: parseFloat(get(values, month).replace(/,/g, "")),
        };

        return target;
      });
    } else if (
      get(values, "allocation.value") === "biannual" ||
      get(values, "allocation.value") === "biannual-constant" ||
      get(values, "allocation.value") === "prorata-biannual"
    ) {
      return ["half1", "half2"].map((i, index) => {
        const date = moment(get(period[0], "start_date")).format("YYYY");
        if (i === "half1") {
          const target = {
            end_date: moment(date)
              .quarter(2)
              .endOf("quarter")
              .format("YYYY-MM-DD"),
            label: `${i}`,
            start_date: moment(date)
              .quarter(1)
              .startOf("quarter")
              .format("YYYY-MM-DD"),
            target: parseFloat(get(values, i).replace(/,/g, "")),
          };
          return target;
        } else {
          const target = {
            end_date: moment(date)
              .quarter(4)
              .endOf("quarter")
              .format("YYYY-MM-DD"),
            label: `${i}`,
            start_date: moment(date)
              .quarter(3)
              .startOf("quarter")
              .format("YYYY-MM-DD"),
            target: parseFloat(get(values, i).replace(/,/g, "")),
          };
          return target;
        }
      });
    } else {
      return ["Q1", "Q2", "Q3", "Q4"].map((i, index) => {
        const date = moment(get(period[0], "start_date")).format("YYYY");

        const target = {
          end_date: moment(date)
            .quarter(index + 1)
            .endOf("quarter")
            .format("YYYY-MM-DD"),
          label: `${i}`,
          start_date: moment(date)
            .quarter(index + 1)
            .startOf("quarter")
            .format("YYYY-MM-DD"),
          target: parseFloat(get(values, i).replace(/,/g, "")),
        };

        return target;
      });
    }
  };

  const handleAddKpi = (values) => {
    const payload: any = {
      kpi_id: get(values, "kpi.value"),
      max_score: get(values, "max_score"),
      min_score: get(values, "min_score"),
      target: parseFloat(get(values, "target").replace(/,/g, "")),
      notes: get(values, "notes", ""),
      weight: get(values, "weight"),
      optimisation: get(values, "optimisation.value"),
      target_score: get(values, "target_score"),
      alignment: get(values, "alignment.value"),
      supported_kpi_id: get(values, "supported_kpi.value"),
      kpi_target_type: "annual",
    };
    if (
      get(values, "allocation.value") === "monthly" ||
      get(values, "allocation.value") === "prorata-monthly" ||
      get(values, "allocation.value") === "prorata-quarterly" ||
      get(values, "allocation.value") === "quarterly" ||
      get(values, "allocation.value") === "monthly-constant" ||
      get(values, "allocation.value") === "quarterly-constant" ||
      get(values, "allocation.value") === "biannual-constant" ||
      get(values, "allocation.value") === "biannual" ||
      get(values, "allocation.value") === "prorata-biannual"
    ) {
      payload.targets = getTargets(values);
      payload.kpi_target_type = get(values, "allocation.value");
    }

    if (get(values, "allocation.value") === "annual") {
      const date = moment(get(period[0], "start_date")).format("YYYY");
      payload.targets = [
        {
          start_date: moment(date).startOf("year").format("YYYY-MM-DD"),
          end_date: moment(date).endOf("year").format("YYYY-MM-DD"),
          label: "annual",
          target: parseInt(get(values, "target").replace(/,/g, "")),
        },
      ];
    }
    if (
      (kpiType === "numeric" &&
        get(values, "allocation.value") === "monthly") ||
      (kpiType === "numeric" && get(values, "allocation.value") === "quarterly")
    ) {
      const totalAllocation = getTargets(values).reduce(
        (total, num: any) => total + num.target,
        0
      );

      if (
        totalAllocation !== parseInt(get(values, "target").replace(/,/g, ""))
      ) {
        toast.error(
          totalAllocation > parseInt(get(values, "target").replace(/,/g, ""))
            ? "Allocation targets should not exceed target"
            : "Allocation targets should not be less than target"
        );
        return;
      }
    }
    createReq(`${SCORECARD_URL}/${scorecard_id}/kpi`, payload);
  };

  useEffect(() => {
    const { complete, pending, data, error } = createRes;
    if (error) {
      toast.error(get(data, "message"));
    }
    if (!pending && complete && !error) {
      closeModal();
      setStep(1);
      formRef.current.resetForm(initialValues);
      toast.success("Kpi added successfully");
    }

    return () => {};
  }, [createRes]);

  const editKpi = (values) => {
    console.log(values);

    const payload: any = {
      target: parseFloat(get(values, "target").replace(/,/g, "")),
      weight: get(values, "weight"),
      target_score: get(values, "target_score"),
      alignment: get(values, "alignment.value"),
      notes: get(values, "notes", ""),
      supported_kpi_id: get(values, "supported_kpi.value"),
    };

    if (
      get(values, "allocation.value") === "monthly" ||
      get(values, "allocation.value") === "prorata-monthly" ||
      get(values, "allocation.value") === "prorata-quarterly" ||
      get(values, "allocation.value") === "quarterly" ||
      get(values, "allocation.value") === "monthly-constant" ||
      get(values, "allocation.value") === "quarterly-constant" ||
      get(values, "allocation.value") === "biannual-constant" ||
      get(values, "allocation.value") === "biannual" ||
      get(values, "allocation.value") === "prorata-biannual"
    ) {
      payload.targets = getTargets(values);
      payload.kpi_target_type = get(values, "allocation.value");
    }
    if (get(values, "allocation.value") === "annual") {
      payload.kpi_target_type = get(values, "allocation.value");
      const date = moment(get(period[0], "start_date")).format("YYYY");
      payload.targets = [
        {
          start_date: moment(date).startOf("year").format("YYYY-MM-DD"),
          end_date: moment(date).endOf("year").format("YYYY-MM-DD"),
          label: "annual",
          target: parseInt(get(values, "target").replace(/,/g, "")),
        },
      ];
    }

    if (
      (kpiType === "numeric" &&
        get(values, "allocation.value") === "monthly") ||
      (kpiType === "numeric" && get(values, "allocation.value") === "quarterly")
    ) {
      const totalAllocation = getTargets(values).reduce(
        (total, num: any) => total + num.target,
        0
      );

      if (
        totalAllocation !== parseInt(get(values, "target").replace(/,/g, ""))
      ) {
        toast.error(
          totalAllocation > parseInt(get(values, "target").replace(/,/g, ""))
            ? "Allocation targets should not exceed target"
            : "Allocation targets should not be less than target"
        );
        return;
      }
    }
console.log(editData);

    updateReq(
      `${SCORECARD_URL}/${scorecard_id}/kpi/${editData.kpi_id}`,
      payload
    );
  };
  useEffect(() => {
    const { complete, pending, data, error } = updateRes;
    if (error) toast.error(get(data, "message"));
    if (!pending && complete && !error) {
      toast.success("Kpi updated successfully");
      closeModal();
    }
  }, [updateRes]);
console.log(editData);

  useEffect(() => {
    if (!isAddMode && isEdit) {      
      const newEditData = {
        ...editData,
        kpi_type: {
          value: get(editData, "kpi.kpi_type"),
          label: get(editData, "kpi.kpi_type"),
        },
        optimisation: {
          value: get(editData, "kpi.optimisation"),
          label: get(editData, "kpi.optimisation"),
        },
        score_type: {
          value: get(editData, "kpi.score_type"),
          label: get(editData, "kpi.score_type"),
        },
        alignment: {
          value: get(editData, "alignment"),
          label: get(editData, "alignment"),
        },
        kpi: {
          value: get(editData, "kpi.id"),
          label: get(editData, "kpi.name"),
        },
        allocation: {
          value: get(editData, "kpi_target_type"),
          label: get(editData, "kpi_target_type"),
        },
        supported_kpi: {
          value: get(editData, "supported_kpi.id"),
          label: get(editData, "supported_kpi_name"),
        },
      }; 
      const fields = [
        "kpi",
        "weight",
        "notes",
        "max_score",
        "min_score",
        "min_value",
        "max_value",
        "target",
        "kpi_type",
        "optimisation",
        "score_type",
        "target_score",
        "baseline",
        "alignment",
        "allocation",
        "supported_kpi",
      ];
      let kpiId = get(editData, 'id', []);

      const promises: any = axios.get(
        `${SCORECARD_KPI_TARGET}?scorecard_kpi_id=${kpiId}`)
      promises.then(function (promiseValues) {

        // settargets(promiseValues.data.returned_resultset)
        promiseValues.data.returned_resultset.forEach((target) => {
          formRef.current.setFieldValue(target.label, target.target, false);
        });
        console.log(promiseValues.data.returned_resultset);
        
      });
      
      targets.forEach((target) => {
        formRef.current.setFieldValue(target.label, target.target, false);
      });
      fields.forEach((field) =>
        formRef.current.setFieldValue(field, newEditData[field], false)
      );
    }
  }, [editData, isAddMode, isEdit]);

  const initialValues = {
    kpi: "",
    score: "",
    weight: "",
    target: "",
    notes: "",
    min_score: "",
    max_score: "",
    optimization: "",
    score_type: "",
    target_score: "",
    alignment: "",
    supported_kpi: "",
    allocation: {
      label: "",
      value: "",
    },
  };
  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={isAddMode ? handleAddKpi : editKpi}
      validationSchema={AddKpiSchema}
    >
      {({ handleSubmit, setFieldValue, values }) => (
        <>
          <Header>
            <HeaderItem enabled={step === 1 ? true : false}>
              <ScorecardIcon isActive />
              <TitleText>KPI parameters</TitleText>
            </HeaderItem>
            <HeaderItem enabled={step === 2 ? true : false}>
              <OrganisationTypeIcon isActive />
              <TitleText>Alignment Options</TitleText>
            </HeaderItem>
            <HeaderItem enabled={step === 3 ? true : false}>
              <SettingsIcon isActive />
              <TitleText>Allocation</TitleText>
            </HeaderItem>
          </Header>
          {renderSteps(values, setFieldValue)}
          <Footer>
            <Button text="Cancel" backgroundColor="#fff" color="#214160" />
            <ButtonWithBorder
              disabled={step === 1 ? true : false}
              text="Back"
              onClick={previousStep}
              backgroundColor="#fff"
              color="#214160"
            />
            {step === 3 ? (
              <SmallButton text="Finish" onClick={() => handleSubmit()} />
            ) : (
              <SmallButton text="Next" onClick={nextStep} />
            )}
          </Footer>
        </>
      )}
    </Formik>
  );
};

export default AddKpi;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  /* justify-content: space-between; */
  align-items: center;
  border: 2px solid rgba(76, 92, 251, 0.1);
  border-radius: 1em;
  margin-top: 1em;
  margin-bottom: 1em;
`;

const HeaderItem = styled.button<{ enabled: boolean }>`
  display: flex;
  font-size: 12;
  font-weight: 600;
  align-items: center;
  background-color: ${(props) => (props.enabled ? "#EBEFFF" : "#fff")};
  color: #01A8CE;
  padding: 1em 1.5em 1em 1.5em;
  border-radius: 1em;
  width: 100%;
  box-shadow: 0px 0px 0px transparent;
  border: 0px solid transparent;
  text-shadow: 0px 0px 0px transparent;
  opacity: ${(props) => (props.enabled ? 1 : 0.4)};
  ${TitleText} {
    padding-left: 1em;
    color: #01A8CE;
    font-size: 12px;
  }
  &:focus {
    outline: none;
  }
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1em;
`;

const ButtonWithBorder = styled(Button)<{ disabled: boolean }>`
  border: 2px solid #01A8CE;
  padding: 0.3em 4em;
  margin-left: auto;
  margin-right: 1em;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
`;

const SmallButton = styled(Button)`
  padding: 0.4em 4em;
  border: 2px solid #01A8CE;
`;
